import dayjs from 'dayjs'
import { Form, message, Button, Splitter, Tabs, Typography } from 'antd'
import { Formik, useFormikContext } from 'formik'
import { useCallback, useState, useEffect, useRef } from 'react'
import { createUseStyles, useTheme } from 'react-jss'
import { useNavigate, useLocation } from 'react-router-dom'
import api from '../../api'
import { formElementPropsGenerator } from '../../lib/forms'
import { Yup } from '../../lib/validations'
import { ListError, PageHeader } from '../Common'
import { SubmitButton, UploadFormikFile } from '../Forms'
import PatientSelect from '../Forms/PatientSelect'
import PDFPreview from '../Common/PDFPreview'
import { useAsync, useIsMobile } from '../../hooks'
import { MOBILE_MEDIA_QUERY } from '../../lib/responsive'
import { useIsAdmin, useCurrentUserPracticeId } from '../../redux/slices/session'
import { useSelectedPractice } from '../../context/PracticeContext'
import AlertForm from './AlertForm'
import AdditionalPdfDropzone from './AdditionalPdfDropzone'
import { toPath } from '../../routes'

const { TabPane } = Tabs
const { Title } = Typography

const propsFor = formElementPropsGenerator({
  formName: 'alerts-form',
  labels: { patient: 'Patient' },
})

const validationSchema = Yup.object().shape({
  patient: Yup.object().required(),
  fileToUpload: Yup.mixed().required('You need to provide a full report'),
})

const useStyles = createUseStyles({
  pdfBox: {
    maxWidth: 'calc(100% - 456px)',
    flexGrow: '1',
    minWidth: '100%',
    position: 'relative',
    height: 'calc(100vh - 96px - 68px)',
    overflowY: 'scroll',
    overflowX: 'hidden',
    '& canvas': {
      maxWidth: '100%',
    },
    // Each pane is the whole page below md, so a viewport-tall scroll box
    // inside the page scroll would just be two scrollbars on one gesture.
    [MOBILE_MEDIA_QUERY]: {
      maxWidth: 'none',
      height: 'auto',
      overflowY: 'visible',
    },
  },
  additionalPdfHeader: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 12,
  },
  formBox: {
    position: 'relative',
    overflowY: 'scroll',
    height: 'calc(100vh - 96px - 68px)',
    padding: '0 16px',
    '& > form': {
      marginTop: '-16px',
    },
    [MOBILE_MEDIA_QUERY]: {
      height: 'auto',
      overflowY: 'visible',
      padding: 0,
    },
  },
})

const labelCol = {
  sm: { span: 4, offset: 2 },
}

const wrapperCol = {
  sm: { span: 12 },
}

const actionsWrapperCol = {
  sm: { span: 4, offset: labelCol.sm.span + labelCol.sm.offset },
}

// eslint-disable-next-line react/prop-types
function ClearPatientOnPracticeChange({ practiceId, deepLinkedPatient }) {
  const { values, setFieldValue } = useFormikContext()
  const previousPracticeIdRef = useRef(practiceId)

  useEffect(() => {
    const previous = previousPracticeIdRef.current
    previousPracticeIdRef.current = practiceId
    if (previous === practiceId) return
    if (!values.patient) return
    // Deep-link flow: the header practice was just synced to match the loaded
    // patient's practice — keeping the value avoids clobbering the URL-driven
    // selection right after mount.
    if (deepLinkedPatient?.practice?.id === practiceId) return
    setFieldValue('patient', undefined)
  }, [practiceId, deepLinkedPatient, values.patient, setFieldValue])

  return null
}

// eslint-disable-next-line react/prop-types
function AlertFormView({ patientId, fileToUpload, transmissionId, additionalFile, returnTo }) {
  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const from = searchParams.get('from')
  const redirectTo = from === 'patient'
    ? toPath.patients.patient({ id: patientId })
    : returnTo || toPath.alerts()

  const getForm = useCallback(async () => {
    try {
      const patient = await api.patients.get(patientId)
      const form = await api.alerts.getForm(patient.currentDevice.id)
      return form
    } catch (err) {
      message.error(`There was a problem: ${err.message}`)
      return undefined
    }
  }, [patientId])
  const { error, pending, value: form } = useAsync(getForm)

  const handleCreateAlert = useCallback(
    async ({ values, critical }) => {
      const fd = new FormData()
      fd.append('patient_id', patientId)
      fd.append('critical', critical)
      fd.append('values', JSON.stringify(values))

      if (transmissionId) {
        fd.append('transmission_id', transmissionId)
      } else {
        fd.append('device_report', fileToUpload[0])
      }

      if (additionalFile) {
        fd.append('additional_file', additionalFile)
      }

      try {
        await api.alerts.create(fd)
        message.success('Alert successfully created')
        navigate(redirectTo)
      } catch (err) {
        message.error(
          'Oops! There was an error submitting your alert. Please try again',
        )
      }
    },
    [additionalFile, fileToUpload, navigate, patientId, redirectTo, transmissionId],
  )

  const classes = useStyles()

  if (error) {
    return (
      <ListError
        message="There was an error loading this alert"
        description={error.message}
      />
    )
  }

  if (pending) return <div>Loading...</div>

  return (
    <div className={classes.formBox}>
      <AlertForm form={form} onSubmit={handleCreateAlert} />
    </div>
  )
}

export default function New() {
  const classes = useStyles()
  const { brandPrimary } = useTheme()
  const isMobile = useIsMobile()
  const isAdmin = useIsAdmin()
  const userPracticeId = useCurrentUserPracticeId()
  const { selectedPracticeId, setSelectedPracticeId } = useSelectedPractice()
  const practiceId = isAdmin ? selectedPracticeId : userPracticeId

  const [formShow, setFormShow] = useState(false)
  const [patient, setPatient] = useState(undefined)
  const [patientId, setPatientId] = useState(undefined)
  const [transmissionId, setTransmissionId] = useState(null)
  const [fileToUpload, setFileToUpload] = useState(null)
  const [additionalFile, setAdditionalFile] = useState(null)
  const [returnTo, setReturnTo] = useState(null)

  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const patientIdQuery = searchParams.get('patientId')
  const from = searchParams.get('from')
  const redirectTo = from === 'patient'
    ? toPath.patients.patient({ id: patientIdQuery })
    : returnTo || toPath.alerts()

  // Follow the incoming patient/transmission into the header selector so the
  // form's practiceId matches what the user is actually working on.
  const syncSelectedPractice = useCallback(
    id => {
      if (!isAdmin || !id || id === selectedPracticeId) return
      setSelectedPracticeId(id)
    },
    [isAdmin, selectedPracticeId, setSelectedPracticeId],
  )

  const getTransmission = useCallback(async () => {
    if (!transmissionId) return

    const queryTr = await api.transmissions.get(transmissionId)

    const fullUrl = queryTr.fileUrl.startsWith('http')
      ? queryTr.fileUrl
      : `${import.meta.env.VITE_APP_API_URL}${queryTr.fileUrl}`

    setPatientId(queryTr.patient.id)
    setPatient(queryTr.patient)
    syncSelectedPractice(queryTr.practice?.id)
    setFileToUpload([fullUrl])
    setFormShow(true)
  }, [transmissionId, syncSelectedPractice])

  const getPatient = useCallback(
    async id => {
      if (!id) return
      const response = await api.patients.get(id)
      setPatient(response)
      setPatientId(response.id)
      syncSelectedPractice(response.practice?.id)
    },
    [syncSelectedPractice],
  )

  useEffect(() => {
    if (patientIdQuery) {
      getPatient(patientIdQuery)
    }
  }, [getPatient, patientId, patientIdQuery])

  const navigate = useNavigate()
  const handleFirstForm = values => {
    setPatient(values.patient)
    setPatientId(values.patient?.key)
    setFileToUpload(values.fileToUpload)
    setFormShow(true)
  }

  useEffect(() => {
    const alertInfo = JSON.parse(sessionStorage.getItem('alertInformation'))
    sessionStorage.removeItem('alertInformation')

    if (alertInfo?.transmissionId) {
      setTransmissionId(alertInfo.transmissionId)
    }
    if (alertInfo?.returnTo) {
      setReturnTo(alertInfo.returnTo)
    }
  }, [])

  useAsync(getTransmission)

  const patientLabel = patient
    ? `${patient.firstName} ${patient.lastName} - ${dayjs(
        patient.birthDate,
      ).format('MM-DD-YYYY')}`
    : ''

  if (formShow) {
    const header = (
      <PageHeader title="New Alert" onBack={() => navigate(redirectTo)} />
    )

    const formPane = (
      <AlertFormView
        patientId={patientId}
        fileToUpload={fileToUpload}
        transmissionId={transmissionId}
        additionalFile={additionalFile}
        returnTo={returnTo}
      />
    )

    const additionalPdfPane = additionalFile ? (
      <>
        <div className={classes.additionalPdfHeader}>
          <Button size="small" onClick={() => setAdditionalFile(null)}>
            Remove
          </Button>
        </div>
        <PDFPreview url={additionalFile} perPage={10} />
      </>
    ) : (
      <AdditionalPdfDropzone onSelect={setAdditionalFile} />
    )

    // Two splitter panels on a phone leave neither the report nor the form
    // wide enough to use. Below md they are tabs, form first, and the header
    // moves above them — inside a tab it would only show on one of the two.
    if (isMobile) {
      return (
        <div>
          {header}
          <Tabs defaultActiveKey="form">
            <TabPane tab="Alert Form" key="form">
              {formPane}
            </TabPane>
            {fileToUpload && (
              <TabPane tab="Transmission" key="transmission">
                <div className={classes.pdfBox}>
                  <PDFPreview url={fileToUpload[0]} perPage={10} />
                </div>
              </TabPane>
            )}
            <TabPane tab="Additional PDF" key="additional-pdf">
              <div className={classes.pdfBox}>{additionalPdfPane}</div>
            </TabPane>
          </Tabs>
        </div>
      )
    }

    return (
      <Splitter>
        <Splitter.Panel defaultSize="65%" min="10%">
          <div className={classes.pdfBox}>
            {header}
            <Tabs defaultActiveKey="transmission" className="tabs">
              {fileToUpload && (
                <TabPane tab="Transmission" key="transmission">
                  <PDFPreview url={fileToUpload[0]} perPage={10} />
                </TabPane>
              )}
              <TabPane tab="Additional PDF" key="additional-pdf">
                {additionalPdfPane}
              </TabPane>
            </Tabs>
          </div>
        </Splitter.Panel>
        <Splitter.Panel>{formPane}</Splitter.Panel>
      </Splitter>
    )
  }

  if (!practiceId) {
    return (
      <div>
        <PageHeader title="New Alert" onBack={() => navigate(redirectTo)} />
        <Title level={4} style={{ color: brandPrimary }}>
          Please select a practice from the header to continue.
        </Title>
      </div>
    )
  }

  return (
    <div>
      <PageHeader title="New Alert" onBack={() => navigate(redirectTo)} />
      <Formik
        initialValues={{
          patient: patient?.id ? { key: patient.id } : undefined,
          fileToUpload,
        }}
        onSubmit={handleFirstForm}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({
          isSubmitting,
          handleSubmit,
          isValid,
          submitCount,
          values,
          setFieldValue,
        }) => (
          <Form
            onFinish={handleSubmit}
            labelCol={labelCol}
            wrapperCol={wrapperCol}
          >
            <ClearPatientOnPracticeChange
              practiceId={practiceId}
              deepLinkedPatient={patient}
            />
            <PatientSelect
              {...propsFor('patient')}
              practiceId={practiceId}
              labelInValue
              allowClear={false}
              style={{ width: '100%' }}
              disabled={!practiceId}
              onChange={value => {
                setFieldValue('patient', value)
              }}
              options={
                patientIdQuery === values?.patient?.key
                  ? [{ id: patientIdQuery, name: patientLabel }]
                  : undefined
              }
            />
            <UploadFormikFile
              name="fileToUpload"
              label="Full Report"
              supportFiles={['application/pdf']}
            />
            <SubmitButton
              title="Next"
              isValid={isValid}
              submitCount={submitCount}
              isSubmitting={isSubmitting}
              wrapperCol={actionsWrapperCol}
            />
          </Form>
        )}
      </Formik>
    </div>
  )
}
